import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { BaseSettingsUtils } from '@shared/commons/UtilsClasses/BaseGeneralSettingsUtilsWrapper';
import { MathUtils } from '@shared/utils/MathUtils';
import { NumericUtils } from '@shared/utils/NumericUtils';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { instrumentLotsUtils } from '@shared/commons/UtilsClasses/InstrumentLotsUtils';
export class Quantity {
    constructor(value, inLots) {
        this.value = value;
        this.inLots = inLots;
    }
    // TODO. Refactor.
    static toLots(quantityObject, instrument, acc, pT) {
        if (quantityObject.inLots) {
            return quantityObject.value;
        }
        const decimalPlaces = MathUtils.getPrecision(instrument.getLotStep(pT, acc));
        return MathUtils.Round(quantityObject.value / instrument.LotSize, decimalPlaces);
    }
    // TODO. Refactor.
    static convertQuantityValue(quantityObject, instrument, resultInlots, acc, pT) {
        if (resultInlots) {
            return Quantity.toLots(quantityObject, instrument, acc, pT);
        }
        if (!quantityObject.inLots) {
            return quantityObject.value;
        }
        const decimalPlaces = MathUtils.getPrecision(instrument.getLotStep(pT, acc));
        return MathUtils.Round(quantityObject.value * instrument.LotSize, decimalPlaces);
    }
    toString(instrument) {
        if (instrument == null) {
            return this.value.toString();
        }
        const inLots = BaseSettingsUtils.displayAmountInLots();
        const result = Quantity.convertQuantityValue(this, instrument, inLots); // no references of method .toString(instrument) were found
        return result.toString();
    }
    toNumber(instrument) {
        if (instrument === null) {
            return this.value;
        }
        const inLots = BaseSettingsUtils.displayAmountInLots();
        return Quantity.convertQuantityValue(this, instrument, inLots);
    }
    static getQuantityNumericSettings(instrument, account, inLots, productType) {
        if (isNullOrUndefined(instrument)) {
            return QuantityNumericSettings.GetNullSettings();
        }
        const instrumentDefaultSettings = BaseSettings.insDefSettingsStorage.GetInstrumentSettings(instrument);
        const minLot = instrument.getMinLot(productType, account);
        const maxLot = instrument.getMaxLot(productType, account);
        const lotsMultiplier = inLots ? 1 : instrument.LotSize;
        let minValue = minLot * lotsMultiplier;
        const prec = instrument.getAmountPrecision(inLots, account, productType);
        if (prec === 0 && Math.round(minValue) <= 0) {
            minValue = 1;
        }
        let value;
        let step;
        let incrementCoefficient;
        if (isNullOrUndefined(instrumentDefaultSettings)) {
            value = minLot * lotsMultiplier;
            step = instrument.getLotStep(productType, account) * lotsMultiplier;
            incrementCoefficient = 1;
        }
        else {
            let minDefLotVal = null;
            const defaultLots = instrumentDefaultSettings.DefaultLots;
            const len = defaultLots.length;
            for (let i = 0; i < len; i++) {
                const defLot = defaultLots[i];
                if (!defLot.Selected)
                    continue;
                const defLotVal = defLot.Lots;
                if (minDefLotVal === null || parseFloat(defLotVal) < parseFloat(minDefLotVal)) {
                    minDefLotVal = defLotVal;
                }
            }
            if (minDefLotVal !== null) {
                value = minDefLotVal * lotsMultiplier;
            }
            step = instrument.getLotStep(productType, account) * lotsMultiplier;
            incrementCoefficient = instrumentDefaultSettings.UserQuantityCoef;
        }
        // +++ http://tp.pfsoft.net/entity/21325
        value = MathUtils.RoundTo(value, step);
        if (value < minValue) {
            value = minValue;
        }
        let defaultLots = [];
        if (isValidArray(instrumentDefaultSettings === null || instrumentDefaultSettings === void 0 ? void 0 : instrumentDefaultSettings.DefaultLots)) {
            defaultLots = instrumentLotsUtils.getSortedDefaultLots(instrumentDefaultSettings.DefaultLots, instrument.LotSize, prec, inLots);
        }
        return new QuantityNumericSettings(value, minValue, maxLot * lotsMultiplier, step, incrementCoefficient, prec, defaultLots);
    }
    static getCashNumericSettings(instrument, account) {
        var _a;
        const asset = (_a = account === null || account === void 0 ? void 0 : account.GetAssetBalanceCorrect(instrument)) === null || _a === void 0 ? void 0 : _a.Asset;
        if (asset == null) {
            return QuantityNumericSettings.GetNullSettings();
        }
        const min = asset.MinChange;
        return new QuantityNumericSettings(1, min, NumericUtils.MAXVALUE, min, 1, asset.Point, []);
    }
    static adjustDefault(instrument, account, productType, isQuantityInLots, savedQuantity) {
        const settings = Quantity.getBaseSettings(instrument, productType, account, isQuantityInLots);
        let value = settings.minValue;
        if (!isNullOrUndefined(savedQuantity)) {
            value = Quantity.convertQuantityValue(savedQuantity, instrument, isQuantityInLots, account, productType);
            value = Math.max(settings.minValue, Math.min(MathUtils.RoundDouble(value, settings.decimalPrecision), settings.maxValue));
        }
        const formatted = PriceFormatter.formatPrice(value, settings.decimalPrecision);
        return { value, formatted, settings };
    }
    static convertAmountToLots(amountValue, instrument, useSettings = true, acc, pT) {
        if (useSettings && BaseSettingsUtils.displayAmountInLots()) {
            return amountValue;
        }
        else {
            return MathUtils.Round(amountValue / instrument.LotSize, instrument.getLotStepPrecision(pT, acc));
        }
    }
    static convertLotsToAmount(lotsValue, instrument, useSettings = true, acc, pT) {
        if (useSettings && BaseSettingsUtils.displayAmountInLots()) {
            return lotsValue;
        }
        else {
            return MathUtils.Round(lotsValue * instrument.LotSize, MathUtils.getPrecision(instrument.getLotStep(pT, acc)));
        }
    }
    static getBaseSettings(ins, pT, acc, inLots) {
        const lotStep = ins.getLotStep(pT, acc);
        const lotSize = ins.getLotSize();
        let minValue = Math.max(ins.getMinLot(pT, acc), lotStep);
        let maxValue = ins.getMaxLot(pT, acc);
        let step = lotStep;
        let decimalPrecision = ins.getLotStepPrecision(pT, acc);
        if (!inLots) {
            minValue *= lotSize;
            maxValue *= lotSize;
            step *= lotSize;
            decimalPrecision = MathUtils.GetDecimalPlaces(step);
        }
        return new QuantityNumericSettings(minValue, minValue, maxValue, step, 1, decimalPrecision, []);
    }
}
export class QuantityNumericSettings {
    constructor(value, minValue, maxValue, step, incrementCoefficient, decimalPrecision, defaultLots) {
        this.value = value;
        this.minValue = minValue;
        this.maxValue = maxValue;
        this.step = step;
        this.incrementCoefficient = incrementCoefficient;
        this.decimalPrecision = decimalPrecision;
        this.defaultLots = defaultLots;
    }
    static GetNullSettings() {
        return new QuantityNumericSettings(undefined, undefined, undefined, undefined, undefined, undefined, []);
    }
}
